"use client";
import React, { useState, useEffect } from "react";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useAttendanceReport } from "../../context/AttendanceReportContext";
export default function AttendanceReport() {
  const { reports, loading, error, fetchAttendanceReports } = useAttendanceReport();

  // 🔹 Current month/year
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

  // Filters (default to current month/year)
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [monthFilter, setMonthFilter] = useState(currentMonth);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const monthNames = ["", "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // 🔹 Flatten and filter data
  const filteredData = reports
    .flatMap(user =>
      user.months.map(month => ({
        id: user.user_id,
        employee_name: user.employee_name,
        month: month.month.split("-")[1], // "2026-04" -> "04"
        year: Number(month.month.split("-")[0]), // "2026-04" -> 2026
        presents: month.presents_count,
        absents: month.absents_count,
        half_days_count : month.half_days_count,
        lateMinutes: month.late_minutes
      }))
    )
    .filter(emp => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = emp.employee_name.toLowerCase().includes(search) || String(emp.id).includes(search);
      const matchesYear = yearFilter ? emp.year === Number(yearFilter) : true;
      const matchesMonth = monthFilter ? emp.month === monthFilter : true;
      return matchesSearch && matchesYear && matchesMonth;
    });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const currentRows = filteredData.slice(startIndex, startIndex + rowsPerPage);

  // Fetch data on mount
  useEffect(() => {
    fetchAttendanceReports();
  }, []);

  return (
    <div className="bg-sky-50 px-6 py-6 sm:px-9 max-sm:px-4">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Attendance Report</h1>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">
        <input
          type="text"
          placeholder="Search by name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="outline-none text-sm flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-auto transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
        />
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={yearFilter}
            onChange={(e) => { setYearFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
          >
            <option value="">All Years</option>
            {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
          >
            <option value="">All Months</option>
            {monthNames.slice(1).map((name, idx) => (
              <option key={idx+1} value={String(idx+1).padStart(2,"0")}>{name}</option>
            ))}
          </select>

          {(searchTerm || yearFilter !== currentYear || monthFilter !== currentMonth) && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setYearFilter(currentYear);
                setMonthFilter(currentMonth);
                setPage(1);
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-center py-6 text-gray-500">Loading attendance reports...</p>
          ) : error ? (
            <p className="text-center py-6 text-red-500">{error}</p>
          ) : (
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="bg-sky-100 text-gray-700">
                  <th className="py-3 px-4 text-left font-semibold">ID</th>
                  <th className="py-3 px-4 text-left font-semibold">Employee Name</th>
                  <th className="py-3 px-4 text-left font-semibold">Month</th>
                  <th className="py-3 px-4 text-left font-semibold">Present Count</th>
                  <th className="py-3 px-4 text-left font-semibold">Absents Count</th>
                  <th className="py-3 px-4 text-left font-semibold">Half Days Count</th>
                  <th className="py-3 px-4 text-left font-semibold">Late Minutes</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.length > 0 ? currentRows.map((emp, idx) => (
                  <tr key={emp.id + idx} className={`border-b hover:bg-gray-50 transition ${idx % 2 === 1 ? "bg-[#f7f6fe]" : ""}`}>
                    <td className="py-3 px-4 text-indigo-600">{startIndex + idx + 1}</td>
                    <td className="py-3 px-4">{emp.employee_name}</td>
                    <td className="py-3 px-4">{monthNames[Number(emp.month)]} {emp.year}</td>
                    <td className="py-3 px-4">{emp.presents}</td>
                    <td className="py-3 px-4">{emp.absents}</td>
                    <td className="py-3 px-4">{emp.half_days_count}</td>
                    <td className="py-3 px-4">{emp.lateMinutes}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-500">No attendance records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="border rounded-md px-2 py-1 outline-none transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              >
                {[5,10,15].map(num => <option key={num} value={num}>{num}</option>)}
              </select>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>
              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                <MdKeyboardArrowLeft size={20} />
              </button>

              {(() => {
                let startPage = Math.max(page - 1, 1);
                let endPage = startPage + 2;
                if(endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(endPage - 2, 1);
                }
                const pages = [];
                for(let i=startPage; i<=endPage; i++){
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${i===page ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
              >
                <MdKeyboardArrowRight size={20} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}